import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import { ContentArticleTemplateContext } from '@contexts/content-article-template'
import { GridOptionsContext } from '@contexts/grid-options'
import { ContentType } from '@entries/content-types'
import ContentfulCtaSecondary from '@entries/contentful-cta-secondary'
import ContentfulCtaSecondaryRefresh from '@entries/contentful-cta-secondary-refresh'
import ContentfulFootnoteSup from '@entries/contentful-footnote-sup'
import ContentfulInternalLinkButton from '@entries/contentful-internal-link-button'
import ContentfulPageSectionAccordion from '@entries/contentful-page-section-accordion'
import ContentfulPageSectionBento from '@entries/contentful-page-section-bento'
import ContentfulPageSectionCarousel from '@entries/contentful-page-section-carousel'
import ContentfulPageSectionGridLayout from '@entries/contentful-page-section-grid-layout'
import ContentfulPageSectionImageLeft from '@entries/contentful-page-section-image-left'
import ContentfulPageSectionPictoRight from '@entries/contentful-page-section-picto-right'
import ContentfulPageSectionRecommendation from '@entries/contentful-page-section-recommendation'
import ContentfulPageSectionStars from '@entries/contentful-page-section-stars'
import ContentfulPageSectionSummaryAccordion from '@entries/contentful-page-section-summary-accordion'
import ContentfulPageSectionTabs from '@entries/contentful-page-section-tabs'
import contentfulPdfCard from '@entries/contentful-pdf-card'
import { withDisplayName } from '@hocs/with-display-name'
import { bold } from '@render-mark/bold'
import { documentGrid } from '@render-node/document-grid'
import { documentSubtitle } from '@render-node/document-subtitle'
import { heading3Green } from '@render-node/heading-3-green'
import { heading3GreenSpan } from '@render-node/heading-3-green-span'
import { paragraphNoGutter } from '@render-node/paragraph-no-gutter'
import { spanGreen } from '@render-node/span-green'
import { children } from '@render-node/utils/children'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import { ContentfulContentArticleTemplate } from '@typings/contentful-content-article-template'
import { ContentfulPageSectionTemplate } from '@typings/contentful-page-section-template'
import { SectionTemplate } from '@ui/section-template'
import { clsx } from 'clsx'
import { graphql } from 'gatsby'
import React from 'react'

const contentArticleTemplateMapping: any = {
  [ContentfulPageSectionTemplate.CAROUSEL]:
    ContentfulContentArticleTemplate.CARD_IMAGE_FULL,
  [ContentfulPageSectionTemplate.DEFAULT]:
    ContentfulContentArticleTemplate.DEFAULT,
}

const ContentfulPageSection: Utils.EntryFC<
  Queries.ContentfulPageSectionFragment
> = ({ content, entryInspectorProps, fieldInspectorProps }) => {
  const {
    heading,
    subheading,
    templateStyle,
    ctaRef,
    contentArticleTemplate,
    template,
    anchor,
    contents,
    bottomText,
  } = content

  const isRefreshSection =
    templateStyle.includes('refresh') ||
    templateStyle.includes('noir-premium') ||
    templateStyle.includes('iconic')

  const ContentfulCta = isRefreshSection
    ? ContentfulCtaSecondaryRefresh
    : ContentfulCtaSecondary

  return (
    <ContentArticleTemplateContext.Provider
      value={
        contentArticleTemplate ||
        contentArticleTemplateMapping[template] ||
        contentArticleTemplateMapping[ContentfulPageSectionTemplate.DEFAULT]
      }
    >
      <div
        className="group/section [counter-reset:content-article]"
        data-content-article-template={contentArticleTemplate}
        id={anchor}
        {...entryInspectorProps()}
      >
        <SectionTemplate isEmpty={!contents} style={templateStyle}>
          {(heading || subheading) && (
            <div
              className={clsx(
                'm-auto max-w-[776px]',
                contents ? 'mb-[3.75rem]' : 'mb-0'
              )}
            >
              {heading && (
                <RichText
                  className="text-balance"
                  {...fieldInspectorProps({
                    fieldId: 'heading',
                  })}
                  content={heading}
                  options={{
                    ...defaultRichTextFieldOptionsDeprecated,
                    renderMark: {
                      ...defaultRichTextFieldOptionsDeprecated.renderMark,
                      [MARKS.BOLD]: spanGreen,
                    },
                    renderNode: {
                      ...defaultRichTextFieldOptionsDeprecated.renderNode,
                      [BLOCKS.DOCUMENT]: documentGrid,
                      [BLOCKS.HEADING_2]: children,
                      [BLOCKS.HEADING_3]: heading3Green,
                      [BLOCKS.PARAGRAPH]: children,
                      [INLINES.EMBEDDED_ENTRY]: {
                        [ContentType.ContentfulFootnote]: ContentfulFootnoteSup,
                      },
                    },
                  }}
                />
              )}
              {subheading && (
                <RichText
                  {...fieldInspectorProps({
                    fieldId: 'subheading',
                  })}
                  content={subheading}
                  options={{
                    ...defaultRichTextFieldOptionsDeprecated,
                    renderNode: {
                      ...defaultRichTextFieldOptionsDeprecated.renderNode,
                      [BLOCKS.DOCUMENT]: documentSubtitle,
                      [BLOCKS.HEADING_2]: children,
                      [BLOCKS.HEADING_3]: heading3GreenSpan,
                      [BLOCKS.PARAGRAPH]: paragraphNoGutter,
                      [INLINES.EMBEDDED_ENTRY]: {
                        [ContentType.ContentfulFootnote]: ContentfulFootnoteSup,
                      },
                    },
                  }}
                />
              )}
            </div>
          )}

          <Entry
            content={content}
            mapping={{
              [ContentfulPageSectionTemplate.TWO_COLS]: (props: any) => (
                <GridOptionsContext.Provider
                  value={{
                    col: { tablet: 2 },
                  }}
                >
                  <ContentfulPageSectionGridLayout {...props} />
                </GridOptionsContext.Provider>
              ),
              [ContentfulPageSectionTemplate.THREE_COLS]: (props: any) => (
                <GridOptionsContext.Provider
                  value={{
                    col: {
                      tablet: contents?.length % 2 === 0 ? 2 : 1,
                      laptop: 3,
                    },
                  }}
                >
                  <ContentfulPageSectionGridLayout {...props} />
                </GridOptionsContext.Provider>
              ),
              [ContentfulPageSectionTemplate.FOUR_COLS]: (props: any) => (
                <GridOptionsContext.Provider
                  value={{
                    col: { tablet: 2, laptop: 4 },
                  }}
                >
                  <ContentfulPageSectionGridLayout {...props} />
                </GridOptionsContext.Provider>
              ), // Replace by a content type without template 🎨-component-layout-grid with parameter
              [ContentfulPageSectionTemplate.ACCORDION]:
                ContentfulPageSectionAccordion, // Convert to specific content type accordion
              [ContentfulPageSectionTemplate.OFFER]:
                ContentfulPageSectionSummaryAccordion, // To remove
              [ContentfulPageSectionTemplate.CAROUSEL]:
                ContentfulPageSectionCarousel, // Transform to GRID with maxColumns(sm,md,lg) + carousel option
              [ContentfulPageSectionTemplate.RECOMMENDATION]:
                ContentfulPageSectionRecommendation, // To remove, move this template to content article template config
              [ContentfulPageSectionTemplate.STARS]: ContentfulPageSectionStars,
              [ContentfulPageSectionTemplate.STAGE]: ContentfulPageSectionBento, // remove after refresh
              [ContentfulPageSectionTemplate.BENTO]: ContentfulPageSectionBento,
              [ContentfulPageSectionTemplate.IMAGE_LEFT]:
                ContentfulPageSectionImageLeft,
              [ContentfulPageSectionTemplate.DEFAULT]: (props: any) => (
                <GridOptionsContext.Provider
                  value={{
                    col: { laptop: 1 },
                  }}
                >
                  <ContentfulPageSectionGridLayout {...props} />
                </GridOptionsContext.Provider>
              ),
              [ContentfulPageSectionTemplate.TABS]: ContentfulPageSectionTabs,
              [ContentfulPageSectionTemplate.PICTO_RIGHT]:
                ContentfulPageSectionPictoRight,
            }}
            mappingKeyResolver={({ template }) =>
              template || ContentfulPageSectionTemplate.DEFAULT
            }
          />
          <RichText
            className="mt-8 text-center"
            {...fieldInspectorProps({
              fieldId: 'bottomText',
            })}
            content={bottomText}
            options={{
              renderMark: {
                [MARKS.BOLD]: bold,
              },
              renderNode: {
                [BLOCKS.PARAGRAPH]: paragraphNoGutter,
                [BLOCKS.EMBEDDED_ENTRY]: {
                  [ContentType.ContentfulPdfLink]: contentfulPdfCard,
                },
                [INLINES.EMBEDDED_ENTRY]: {
                  [ContentType.ContentfulFootnote]: ContentfulFootnoteSup,
                },
              },
            }}
          />
          {ctaRef && (
            <div
              className="flex justify-center py-8"
              {...fieldInspectorProps({
                fieldId: 'ctaRef',
              })}
            >
              <Entry
                content={ctaRef}
                mapping={{
                  [ContentType.ContentfulCta]: ContentfulCta,
                  [ContentType.ContentfulInternalLink]:
                    ContentfulInternalLinkButton,
                }}
              />
            </div>
          )}
        </SectionTemplate>
      </div>
    </ContentArticleTemplateContext.Provider>
  )
}

export const query = graphql`
  fragment ContentfulPageSection on ContentfulSection {
    ...ContentfulEntry
    ...ContentfulPageSectionAccordion
    ...ContentfulPageSectionCarousel
    ...ContentfulPageSectionGridLayout
    ...ContentfulPageSectionRecommendation
    ...ContentfulPageSectionBento
    ...ContentfulPageSectionStars
    ...ContentfulPageSectionSummaryAccordion
    ...ContentfulPageSectionTabs
    contentArticleTemplate
    contents {
      ...ContentfulContentArticle
      ...ContentfulLegacyEtapier
      ...ContentfulWrapperRichMedia
      ...ContentfulReview
      ...ContentfulFaqPageList
      ...ContentfulNumbersHighlight
    }
    anchor
    ctaRef {
      ...ContentfulCtaSecondary
      ...ContentfulCtaSecondaryRefresh
      ...ContentfulInternalLinkButton
    }
    template
    templateStyle
    heading {
      rich
      references {
        ...ContentfulFootnoteSup
      }
    }
    subheading {
      rich
      references {
        ...ContentfulFootnoteSup
      }
    }
    bottomText {
      rich
      references {
        ...ContentfulPDFCard
        ...ContentfulFootnoteSup
      }
    }
  }
`

export default withDisplayName(ContentfulPageSection, 'ContentfulPageSection')
